import { useMemo } from 'react';

import { useParams } from 'react-router';

import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import { FieldOwnerState } from '@mui/x-date-pickers';
import { LocalizationProvider, MultiInputDateRangeField } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { ptBR } from 'date-fns/locale';
import { Field, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';

import { CissTextField, MarketingCampanhaFieldFormik, ToggleFgSomenteLojasRevendedor } from 'components/fields/';
import { Row } from 'components/form';
import { Block, FormPage } from 'components/page';
import { useFormContext } from 'context/FormContext';
import { usePermissionsContext } from 'context/PermissionsContext';
import { useFormMutation } from 'mutations/useFormMutation';
import { getTypeMarketing, initialCampanhaFormValues, MarketingTradePage, RequestCampanhaModelProps } from 'pages/marketing-trade/types';

const mapContentToInitialValues = (contentData: RequestCampanhaModelProps): RequestCampanhaModelProps => {
    if (!contentData.fgPermanente) {
        if (!contentData.dtInicioExibicao?.endsWith('T00:00:00')) {
            contentData.dtInicioExibicao = `${contentData.dtInicioExibicao}T00:00:00`;
        }

        if (!contentData.dtFimExibicao?.endsWith('T00:00:00')) {
            contentData.dtFimExibicao = `${contentData.dtFimExibicao}T00:00:00`;
        }
    }

    return contentData;
};

export function MarketingTradeCampanhaForm({ isMarketing = false }: MarketingTradePage) {
    const { setContent } = useFormContext();
    const { hasPermission } = usePermissionsContext();
    const { mutate } = useFormMutation();
    const { id } = useParams();
    const hasModeratePermission = hasPermission([isMarketing ? 'MARKETING_CAMPANHA_MODERAR' : 'TRADE_CAMPANHA_MODERAR']);

    const url = '/gestao/campanhamarketing';

    const handleSubmit = (values: RequestCampanhaModelProps, formik: FormikHelpers<RequestCampanhaModelProps>): void => {
        if (values.fgPermanente) {
            values.dtInicioExibicao = null;
            values.dtFimExibicao = null;
        }

        mutate(
            {
                url,
                formik,
                idField: 'idCampanhaMarketing',
                formData: values,
            },
            {
                onSuccess: (response: any) => {
                    const { data } = response.data;

                    if (data) {
                        setContent(data);
                    }
                },
            },
        );
    };

    const validationSchema = useMemo(
        (): any =>
            Yup.object({
                dsCampanhaMarketing: Yup.string().required().min(3),
                dtInicioExibicao: Yup.date().when('fgPermanente', {
                    is: true,
                    then: (schema) => schema.nullable(),
                    otherwise: (schema) => schema.typeError('A data não é válida.').required(),
                }),
                dtFimExibicao: Yup.date().when('fgPermanente', {
                    is: true,
                    then: (schema) => schema.nullable(),
                    otherwise: (schema) => schema.typeError('A data não é válida.').required(),
                }),
            }),
        [],
    );

    return (
        <FormPage
            title={`Campanha de ${isMarketing ? 'Marketing' : 'Trade'}`}
            validateOnSubmit={true}
            validateOnChange={false}
            url={url}
            onSubmit={handleSubmit}
            fgAtivo={hasModeratePermission}
            validationSchema={validationSchema}
            values={initialCampanhaFormValues(isMarketing)}
            mapContentToInitialValues={mapContentToInitialValues}
            extraContent={hasModeratePermission ? <ToggleFgSomenteLojasRevendedor /> : null}
        >
            {(formik: FormikProps<RequestCampanhaModelProps>) => (
                <Block>
                    <Row>
                        <Grid sx={{ flex: 1 }}>
                            <CissTextField
                                label="Nome da Campanha"
                                name="dsCampanhaMarketing"
                                value={formik.values.dsCampanhaMarketing}
                                onChange={(e) => formik.setFieldValue('dsCampanhaMarketing', e.target.value)}
                                error={formik.errors.dsCampanhaMarketing}
                            />
                        </Grid>

                        <Grid sx={{ flex: 1 }}>
                            <Field
                                component={MarketingCampanhaFieldFormik}
                                name="campanhaMarketingPai"
                                limitViewLevelsByCampanha={id || true}
                                filter={[
                                    {
                                        property: 'idTipoMarketing',
                                        operator: 'eq',
                                        value: getTypeMarketing(isMarketing),
                                    },
                                ]}
                            />
                        </Grid>
                    </Row>

                    <Row sx={{ alignItems: 'center' }}>
                        <Grid>
                            <Field as={FormControlLabel} type="checkbox" name="fgPermanente" control={<Checkbox />} label="Campanha Permanente" />
                        </Grid>

                        {!formik.values.fgPermanente && (
                            <Grid sx={{ width: '100%', flex: 1 }}>
                                <LocalizationProvider
                                    adapterLocale={ptBR}
                                    dateAdapter={AdapterDateFns}
                                    localeText={{ start: 'Início Exibição', end: 'Final Exibição' }}
                                >
                                    <DateRangePicker
                                        value={[
                                            formik.values.dtInicioExibicao ? new Date(formik.values.dtInicioExibicao) : null,
                                            formik.values.dtFimExibicao ? new Date(formik.values.dtFimExibicao) : null,
                                        ]}
                                        onChange={(newValue) => {
                                            const [start, end] = newValue;

                                            formik.setFieldValue('dtInicioExibicao', start);
                                            formik.setFieldValue('dtFimExibicao', end);
                                        }}
                                        slots={{ field: MultiInputDateRangeField }}
                                        slotProps={{
                                            field: { dateSeparator: 'até' },
                                            textField: ({ position }: FieldOwnerState & { position?: 'start' | 'end' }) => ({
                                                name: position === 'start' ? 'dtInicioExibicao' : 'dtFimExibicao',
                                                error:
                                                    position === 'start'
                                                        ? Boolean(formik.errors.dtInicioExibicao)
                                                        : Boolean(formik.errors.dtFimExibicao),
                                                helperText: position === 'start' ? formik.errors.dtInicioExibicao : formik.errors.dtFimExibicao,
                                                autoComplete: 'off',
                                            }),
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        )}
                    </Row>
                </Block>
            )}
        </FormPage>
    );
}
